import { useMemo, useState, useContext } from "react";
import { SearchFunctionality } from "./SearchFunctionality.jsx";
import { SearchResultsSection } from "./SearchResultsSection.jsx";
import { Button } from "/src/modern/components/ui/button.tsx";
import MySwivuGroupSection from "./MySwivuGroupSection.jsx";
import { openSwivuAssistPopup } from "@/other/utils/utils.jsx";
import { SWIVU_ASSIST_CONFIG } from "@/other/constants/OtherKeys.jsx";
import { FilterPills } from "./FilterPills.jsx";
import { useBoardCardActions } from "@/react/hooks/useBoardCardActions.jsx";
import { useBoardData } from "@/react/hooks/useBoardData.jsx";
import AuthContext from "@/react/context/authContext.jsx";
import AppUserAvatarButton
  from "@/react/components/appHeader/headerButtons/appUserAvatarButton/AppUserAvatarButton.jsx";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { ChangelogBell } from "@/react/components/changelog";

const filterUtils = {
  // Text search filter
  byText: (query) => (item) =>
    !query.trim() || item.searchableText.includes(query.trim().toLowerCase()),

  // Board type filter
  byBoardType: (boardTypes) => (item) =>
    boardTypes.length === 0 || boardTypes.includes(item.category),

  // Role filter (boards only)
  byRole: (roles) => (item) => {
    if (roles.length === 0 || item.type !== "board") return true;
    return roles.includes(item.boardData.role);
  },

  // Workforce status filter (boards only)
  byWorkforceStatus: (statuses) => (item) => {
    if (statuses.length === 0 || item.type !== "board") return true;

    const isActive = item.boardData.workforceActive;
    const statusSet = new Set(statuses);

    // If both active and inactive are selected, show all boards
    if (statusSet.has("active") && statusSet.has("inactive")) return true;

    // Otherwise, match the specific status
    return (
      (statusSet.has("active") && isActive) ||
      (statusSet.has("inactive") && !isActive)
    );
  },

  // Category filter (templates only)
  byCategory: (categories) => (item) => {
    if (categories.length === 0 || item.type !== "template") return true;
    return categories.includes(item.templateData.category);
  }
};
const boardTemplates = [
  {
    id: "template-1",
    title: "Cucumber Construction",
    description: `
      <p class="mb-swivu-vertical">Learn how to use Swivu in the construction industry at <a class="!text-[#048ded] underline" href="https://www.cucumberconstruction.com" target="_blank">www.cucumberconstruction.com</a></p>
      <p class="mb-swivu-vertical"> You can manipulate anything on your copy of Cucumber Construction.</p>
      <p class="mb-swivu-vertical">Swivu Test Drives are added to your Swivu board list.</p>
    `,
    category: "Project Management",
    adminEmails: [{ email: "Bruce Warila", image_url: "BW" }]
  }
];

const MySwivuBoards = () => {
  const { editorHandler } = useContext(BryntumContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilters, setSearchFilters] = useState({
    boardTypes: [],
    roles: [],
    workforceStatus: [],
    categories: []
  });
  const { handleAddBoard, handleOnFileUpload, inputRef, handleWorkforceChange } = useBoardCardActions();
  const { isLoading: boardsLoading, categorizedBoards, recentlyViewed, updateRecentlyViewed } = useBoardData();
  const { user } = useContext(AuthContext);

  const isSearchActive = searchQuery.trim() !== "" || searchFilters.boardTypes.length > 0 || searchFilters.roles.length > 0 || searchFilters.workforceStatus.length > 0 || searchFilters.categories.length > 0;
  const allItems = useMemo(() => {
    const items = [
      ...categorizedBoards.created.map((board) => ({
        id: board.id,
        type: "board",
        category: "created",
        boardData: board,
        searchableText: `${board.title || board.name} ${
          board.role
        } ${board.adminEmails
          ?.map((admin) => admin.email)
          .join(" ")}`.toLowerCase()
      })),
      ...categorizedBoards.invited.map((board) => ({
        id: board.id,
        type: "board",
        category: "invited",
        boardData: board,
        searchableText: `${board.title || board.name} ${
          board.role
        } ${board.adminEmails
          ?.map((admin) => admin.email)
          .join(" ")}`.toLowerCase()
      }))
    ];

    // Only include templates if user is not a demo user
    if (!user?.isDemoUser) {
      items.push(...boardTemplates.map((template) => ({
        id: template.id,
        type: "template",
        category: "template",
        templateData: template,
        searchableText:
          `${template.title} ${template.description} ${template.category}`.toLowerCase()
      })));
    }

    return items;
  }, [categorizedBoards, user?.isDemoUser]);

  const filteredItems = useMemo(() => {
    if (!isSearchActive) return [];

    const filters = [
      filterUtils.byText(searchQuery),
      filterUtils.byBoardType(searchFilters.boardTypes),
      filterUtils.byRole(searchFilters.roles),
      filterUtils.byWorkforceStatus(searchFilters.workforceStatus),
      filterUtils.byCategory(searchFilters.categories)
    ];

    return allItems.filter((item) => filters.every((filter) => filter(item)));
  }, [allItems, searchQuery, searchFilters]);

  const onClearSearch = () => {
    setSearchQuery("");
    setSearchFilters({
      boardTypes: [],
      roles: [],
      workforceStatus: [],
      categories: []
    });
  };

  const onSearchChange = (query) => {
    setSearchQuery(query);
  }

  const onFiltersChange = (filters) => {
    setSearchFilters(filters);
  }

  const onClickSwivuAssist = () => {
    openSwivuAssistPopup({ editorHandler, swivuAssistanceConfig: SWIVU_ASSIST_CONFIG.dashboard });
  }

  return (
    <div className="bg-background h-screen p-12 !overflow-y-auto">
      <div className="mx-auto space-y-8">
        <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
          <div className="flex-1 max-w-2xl">
            <SearchFunctionality
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              filters={searchFilters}
              onFiltersChange={onFiltersChange}
              resultCount={filteredItems.length}
              onClearSearch={onClearSearch}
            />
          </div>

          <div className="flex gap-swivu-horizontal">
            <Button
              className="flex-shrink-0 border border-border bg-card text-foreground hover:bg-muted rounded-md"
              onClick={onClickSwivuAssist}
            >
              <i className="fa-solid fa-question"></i>
              Swivu Assist
            </Button>
            <Button
              onClick={handleAddBoard}
              disabled={user?.isDemoUser}
              className={user?.isDemoUser ? "opacity-50 cursor-not-allowed" : ""}
            >
              <i className="fa-solid fa-plus"></i>
              New Board
            </Button>
            <ChangelogBell />
            <AppUserAvatarButton />

            {/* Hidden file input for import */}
            <input
              ref={inputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleOnFileUpload}
            />
          </div>
        </div>
        <FilterPills
          filters={searchFilters}
          onFiltersChange={onFiltersChange}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
        />

        {isSearchActive ? (
          <SearchResultsSection
            results={filteredItems}
            onWorkforceActiveChange={handleWorkforceChange}
          />
        ) : (
          <div className="space-y-8">
            {boardsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">
                    Loading boards...
                  </p>
                </div>
              </div>
            ) : (
              <MySwivuGroupSection
                categorizedBoards={categorizedBoards}
                recentlyViewed={recentlyViewed}
                boardTemplates={user?.isDemoUser ? [] : boardTemplates}
                handleWorkforceChange={handleWorkforceChange}
                onBoardAccess={updateRecentlyViewed}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySwivuBoards;